import { useCallback } from 'react';
import toast from 'react-hot-toast';

import type { WebSocketMessage } from '@famecreator/shared-types/websocket';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { useInvalidateUserData } from '@/shared/hooks/use-user-data';
import { useWebSocketMessages } from '@/shared/hooks/use-websocket-messages';
import { logger } from '@/shared/lib/logger';

export const useCreditUpdates = () => {
  const { setCredits, setCreditsLoading } = useAuthStore();
  const { invalidateUserData } = useInvalidateUserData();

  const handleCreditMessage = useCallback(
    (message: WebSocketMessage) => {
      if (message.creditUpdate) {
        const { amountAdded, totalCredits, type } = message.creditUpdate;

        // Update local state immediately
        setCredits(totalCredits);
        setCreditsLoading(false);

        // Invalidate cached user data so it gets refreshed with new credits
        // This is more efficient than calling /auth/me immediately
        invalidateUserData();

        if (amountAdded > 0) {
          let toastMessage = '';

          switch (type) {
            case 'subscription':
              toastMessage = `🎉 Subscription credits added: +${amountAdded}`;
              break;
            case 'purchase_credits':
              toastMessage = `💳 Credits purchased: +${amountAdded}`;
              break;
            default:
              toastMessage = `✨ Credits added: +${amountAdded}`;
          }

          toast.success(toastMessage, {
            duration: 4000,
            position: 'top-center',
          });
        }

        logger.auth.debug('Credit update received - cache invalidated', {
          amountAdded,
          totalCredits,
          type,
          timestamp: message.creditUpdate.timestamp,
        });
      }
    },
    [setCredits, setCreditsLoading, invalidateUserData],
  );

  useWebSocketMessages({
    enabled: true,
    onMessage: handleCreditMessage,
  });
};
